// ============================================================================
// Pure SharePoint/OneDrive URL parsing (zero runtime imports).
//
// Used by two consumers that cannot share code with the server integrations
// package: the AOP editor's paste-to-chip handler (browser) and the
// server-safe serializer in ./serialize.ts. The hostname rule mirrors
// `parseSharePointSiteUrl` in @quovy/integrations (sharepoint/site-resolution.ts)
// — keep the two in sync if Microsoft adds sovereign-cloud domains.
// ============================================================================

export interface SharePointLink {
  /** The pasted URL, verbatim — the canonical id of the chip. */
  url: string;
  /** Display label: the decoded file/folder name, else site name, else host. */
  label: string;
  /**
   * Decoded server-relative path when derivable (e.g.
   * "/personal/paul_quovy_co/Documents/AR-Recon Demo"). Null for share tokens
   * (/:x:/s/…) and bare site URLs, where the path isn't in the link at all.
   */
  path: string | null;
}

// Tenant hosts are single-label: tenant.sharepoint.com (sites) or
// tenant-my.sharepoint.com (personal OneDrive). Anchored so
// "foo.sharepoint.com.evil.com" can't pass.
const SHAREPOINT_HOST = /^[a-z0-9-]+\.sharepoint\.(com|us|de|cn)$/i;

function decode(component: string): string | null {
  try {
    return decodeURIComponent(component);
  } catch {
    return null;
  }
}

/**
 * The server-relative path encoded in the URL, if any. Three shapes carry one:
 *
 *   1. `?id=/…`      — library views (`/my?id=…`, `Forms/AllItems.aspx?id=…`)
 *   2. `/:w:/r/<p>`  — long-form share links (the letter encodes the app)
 *   3. `/sites|teams|personal/<name>/<doc path>` — direct document URLs
 *
 * Short share tokens (`/:x:/s/<token>`) intentionally return null — the path
 * only exists behind Graph's shares API.
 */
function serverRelativePath(u: URL): string | null {
  const idParam = u.searchParams.get("id");
  if (idParam?.startsWith("/")) return idParam;

  const share = u.pathname.match(/^\/:[a-z]:\/r(\/[^?]*)/i);
  if (share?.[1]) return decode(share[1]);

  const decoded = decode(u.pathname);
  if (!decoded) return null;
  const doc = decoded.match(/^\/(?:sites|teams|personal)\/[^/]+(\/.+)$/i);
  // List/library .aspx pages without an ?id= point at a view, not a document.
  if (doc?.[1] && !doc[1].toLowerCase().endsWith(".aspx")) return decoded;
  return null;
}

function lastSegment(path: string): string | null {
  const segments = path.split("/").filter(Boolean);
  return segments.length > 0 ? (segments[segments.length - 1] ?? null) : null;
}

function fallbackLabel(u: URL): string {
  // Doc.aspx?sourcedoc={guid}&file=Report.docx names the file directly.
  const fileParam = u.searchParams.get("file");
  if (fileParam) return fileParam;
  const decoded = decode(u.pathname) ?? u.pathname;
  const site = decoded.match(/^\/(?:sites|teams|personal)\/([^/]+)/i);
  return site?.[1] ?? u.hostname;
}

/**
 * Parse text as a single SharePoint/OneDrive URL. Returns null unless the
 * trimmed text is exactly one https URL on a *.sharepoint.{com,us,de,cn} host
 * — the caller's signal to render a chip instead of a plain link.
 */
export function parseSharePointLink(text: string): SharePointLink | null {
  const raw = text.trim();
  if (!raw || /\s/.test(raw)) return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" || !SHAREPOINT_HOST.test(u.hostname)) return null;

  const path = serverRelativePath(u);
  const label = (path && lastSegment(path)) || fallbackLabel(u);
  return { url: raw, label, path };
}
